
function hexToRgb(hex, fallback = [15, 92, 94]) {
  const value = String(hex || '').trim();
  const match = /^#?([0-9a-f]{6})$/i.exec(value);
  if (!match) return fallback;
  const raw = match[1];
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function fetchImageDataUrl(url, { token } = {}) {
  if (!url) return null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  try {
    const response = await fetch(url, { headers });
    if (!response.ok) return null;
    return blobToDataUrl(await response.blob());
  } catch (_) {
    // Cross-origin tenant logos may not allow browser fetch. Printing must still
    // succeed without a logo rather than blocking a worker's physical ID.
    return null;
  }
}

function imageFormat(dataUrl) {
  const value = String(dataUrl || '').toLowerCase();
  if (value.startsWith('data:image/png')) return 'PNG';
  if (value.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

function addContainedImage(doc, dataUrl, x, y, w, h) {
  if (!dataUrl) return;
  try {
    const props = doc.getImageProperties(dataUrl);
    const ratio = Math.min(w / props.width, h / props.height);
    const drawW = props.width * ratio;
    const drawH = props.height * ratio;
    doc.addImage(
      dataUrl,
      imageFormat(dataUrl),
      x + ((w - drawW) / 2),
      y + ((h - drawH) / 2),
      drawW,
      drawH,
      undefined,
      'FAST',
    );
  } catch (_) {
    // A missing/non-decodable image should not stop the card from printing.
  }
}

function drawCutMarks(doc, x, y, w, h) {
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.15);
  const d = 3;
  doc.line(x - d, y, x - 0.5, y);
  doc.line(x, y - d, x, y - 0.5);
  doc.line(x + w + 0.5, y, x + w + d, y);
  doc.line(x + w, y - d, x + w, y - 0.5);
  doc.line(x - d, y + h, x - 0.5, y + h);
  doc.line(x, y + h + 0.5, x, y + h + d);
  doc.line(x + w + 0.5, y + h, x + w + d, y + h);
  doc.line(x + w, y + h + 0.5, x + w, y + h + d);
}

function drawFront(doc, staffId, photoDataUrl, logoDataUrl, x, y, w, h) {
  const [r, g, b] = hexToRgb(staffId?.branding?.primary_color);
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');

  doc.setFillColor(r, g, b);
  doc.roundedRect(x, y, w, 16, 2.5, 2.5, 'F');
  doc.rect(x, y + 12, w, 4, 'F');

  if (logoDataUrl) {
    addContainedImage(doc, logoDataUrl, x + 4, y + 3, 15, 9);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const org = staffId?.branding?.organisation_name || 'Osabea Healthcare Solutions Ltd';
  const orgLines = doc.splitTextToSize(org, 30);
  doc.text(orgLines.slice(0, 2), x + 20, y + 6.2, { baseline: 'middle' });

  doc.setFontSize(7);
  doc.text(staffId?.card?.label || 'AGENCY STAFF', x + (w / 2), y + 21, { align: 'center' });

  const photoX = x + 11;
  const photoY = y + 24;
  const photoW = 32;
  const photoH = 36;
  doc.setDrawColor(210, 215, 220);
  doc.setFillColor(245, 247, 248);
  doc.roundedRect(photoX, photoY, photoW, photoH, 2, 2, 'FD');
  addContainedImage(doc, photoDataUrl, photoX + 1, photoY + 1, photoW - 2, photoH - 2);

  doc.setTextColor(25, 36, 43);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(staffId?.display_name || 'Staff member', w - 8);
  doc.text(nameLines.slice(0, 2), x + (w / 2), y + 65, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const roleLines = doc.splitTextToSize(staffId?.role || 'Agency Staff', w - 8);
  doc.text(roleLines.slice(0, 2), x + (w / 2), y + 72, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(r, g, b);
  doc.text(`Staff ID: ${staffId?.staff_id || 'Pending'}`, x + (w / 2), y + 80, { align: 'center' });
}

function drawBack(doc, staffId, qrDataUrl, logoDataUrl, x, y, w, h) {
  const [r, g, b] = hexToRgb(staffId?.branding?.primary_color);
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');

  if (logoDataUrl) {
    addContainedImage(doc, logoDataUrl, x + 16, y + 4, 22, 10);
  }

  doc.setTextColor(25, 36, 43);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Verify this staff ID', x + (w / 2), y + 18, { align: 'center' });

  if (qrDataUrl) {
    doc.addImage(qrDataUrl, 'PNG', x + 12, y + 22, 30, 30, undefined, 'FAST');
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.8);
  doc.setTextColor(75, 85, 99);
  doc.text('Scan for live employment / work status.', x + (w / 2), y + 56, { align: 'center' });

  doc.setFontSize(7);
  doc.setTextColor(25, 36, 43);
  if (staffId?.expires_at) {
    const date = new Date(staffId.expires_at);
    if (!Number.isNaN(date.getTime())) {
      doc.text(`Card expiry: ${date.toLocaleDateString('en-GB')}`, x + (w / 2), y + 63, { align: 'center' });
    }
  }

  const email = staffId?.branding?.support_email || '';
  const phone = staffId?.branding?.support_phone || '';
  doc.setFontSize(6.5);
  if (email) doc.text(email, x + (w / 2), y + 69, { align: 'center' });
  if (phone) doc.text(phone, x + (w / 2), y + 73, { align: 'center' });

  doc.setFontSize(5.8);
  doc.setTextColor(90, 98, 108);
  const propertyText = 'This card remains the property of the issuing organisation. If found, please return it.';
  doc.text(doc.splitTextToSize(propertyText, w - 8), x + (w / 2), y + 79, { align: 'center' });
}

export async function buildStaffIdPrintPdf({
  staffId,
  photoDataUrl = null,
  logoDataUrl = null,
}) {
  if (!staffId?.issued) throw new Error('Staff ID has not been issued yet.');
  if (!staffId?.photo_present || !photoDataUrl) throw new Error('Upload a staff ID photo before printing.');

  // jsPDF pulls in PNG decoding helpers that require browser TextEncoder.
  // Load it only when a print sheet is requested so ordinary worker-dashboard
  // rendering and onboarding tests stay lightweight and environment-safe.
  const pdfModule = await import('jspdf');
  const JsPDF = pdfModule.jsPDF || pdfModule.default?.jsPDF || pdfModule.default;
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = Number(staffId?.card?.width_mm || 54);
  const h = Number(staffId?.card?.height_mm || 85.6);
  const pageW = 210;
  const gap = 18;
  const totalW = (w * 2) + gap;
  const startX = (pageW - totalW) / 2;
  const y = 42;
  const frontX = startX;
  const backX = startX + w + gap;

  let qrDataUrl = null;
  if (staffId.verification_url) {
    // Load QR support only when somebody actually prints. Keeping qrcode out
    // of the worker-dashboard import path also keeps lightweight dashboard
    // rendering/tests independent from browser TextEncoder support.
    const qrModule = await import('qrcode');
    const QRCode = qrModule.default || qrModule;
    qrDataUrl = await QRCode.toDataURL(staffId.verification_url, { margin: 0, width: 360 });
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(30, 41, 59);
  doc.text('Staff ID print sheet', pageW / 2, 18, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Print at Actual Size / 100%. Cut both cards, place back-to-back, then laminate.', pageW / 2, 24, { align: 'center' });

  drawFront(doc, staffId, photoDataUrl, logoDataUrl, frontX, y, w, h);
  drawBack(doc, staffId, qrDataUrl, logoDataUrl, backX, y, w, h);
  drawCutMarks(doc, frontX, y, w, h);
  drawCutMarks(doc, backX, y, w, h);

  doc.setFontSize(7);
  doc.setTextColor(90, 98, 108);
  doc.text('FRONT', frontX + (w / 2), y + h + 7, { align: 'center' });
  doc.text('BACK', backX + (w / 2), y + h + 7, { align: 'center' });

  return doc;
}

export async function downloadStaffIdPrintSheet(args) {
  const doc = await buildStaffIdPrintPdf(args);
  const safeId = String(args?.staffId?.staff_id || 'staff-id').replace(/[^A-Za-z0-9_-]/g, '_');
  doc.save(`${safeId}_print_sheet.pdf`);
}
